import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, MapPin, CheckCircle, XCircle, AlertCircle, Loader2, FileText, Filter, CreditCard, Eye } from 'lucide-react';
import SEO from '../components/SEO';
import { useAuth } from '../contexts/AuthContext';
import { bookingService, Booking } from '../services/bookingService';
import Pagination from '../components/Pagination';
import HeroSection from '../components/HeroSection';
import heroData from '../data/heroData.json';

const Bookings = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>('ALL');

  const statusConfig = {
    pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: AlertCircle },
    approved: { label: 'Approved', color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle },
    rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800 border-red-200', icon: XCircle },
    cancelled: { label: 'Cancelled', color: 'bg-gray-100 text-gray-800 border-gray-200', icon: XCircle },
  };

  const substatusConfig = {
    'pending approval from school': { label: 'Pending School Approval', color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: AlertCircle },
    'pending payment': { label: 'Pending Payment', color: 'bg-orange-100 text-orange-800 border-orange-200', icon: CreditCard },
  };

  useEffect(() => {
    fetchBookings();
  }, [currentPage, statusFilter]);

  const fetchBookings = async () => {
    setLoading(true);
    setError('');
    try {
      // Convert filter to lowercase for API call
      const filterValue = statusFilter === 'ALL' ? undefined : statusFilter.toLowerCase();
      const response = await bookingService.getMyBookings(currentPage, 9, filterValue);
      setBookings(response.data.bookings);
      setTotalPages(response.data.totalPages);
      setTotalCount(response.data.totalCount);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load bookings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const handleCardClick = (booking: Booking) => {
    if (booking._id) {
      navigate(`/bookings/${booking._id}`);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <>
      <SEO
        title="My Bookings - Book My Yoga TTC"
        description="View and manage your yoga teacher training course bookings and applications."
        url="https://bookmyyogattc.com/bookings"
      />
      <div className="pt-20">
      <HeroSection
        title={heroData.bookings.title}
        description={heroData.bookings.description}
        patternId="bookingsPattern"
      />
      </div>
      <div className="min-h-screen bg-fog">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-6"
          >

            {/* Filter */}
            <div className="bg-ivory-50 rounded-xl p-4 border border-ivory-200">
              <div className="flex items-center space-x-4 flex-wrap gap-4">
                <Filter className="h-5 w-5 text-ivory-600" />
                <label htmlFor="statusFilter" className="text-2 font-medium text-ivory-700">
                  Filter by Status:
                </label>
                <select
                  id="statusFilter"
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="px-4 py-2 border border-ivory-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white text-ivory-900"
                >
                  <option value="ALL">All Statuses</option>
                  {Object.entries(statusConfig).map(([key, config]) => (
                    <option key={key} value={key.toUpperCase()}>{config.label}</option>
                  ))}
                </select>
                {totalCount > 0 && (
                  <span className="text-2 text-ivory-600 ml-auto">
                    {totalCount} {totalCount === 1 ? 'booking' : 'bookings'}
                  </span>
                )}
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-2 flex items-center space-x-2">
                <XCircle className="h-5 w-5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* Loading State */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : bookings.length === 0 ? (
              <div className="bg-ivory-50 rounded-xl p-12 text-center border border-ivory-200">
                <Calendar className="h-16 w-16 text-ivory-400 mx-auto mb-4" />
                <h3 className="text-5 font-semibold text-ivory-900 mb-2">No Bookings Found</h3>
                <p className="text-ivory-600">
                  {statusFilter === 'ALL' 
                    ? "You haven't submitted any course applications yet."
                    : `No bookings found with status "${statusConfig[statusFilter.toLowerCase() as keyof typeof statusConfig]?.label || statusFilter}".`}
                </p>
              </div>
            ) : (
              <>
                {/* Bookings Grid - 3 cards per row */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {bookings.map((booking, index) => {
                    const StatusIcon = statusConfig[booking.status]?.icon || FileText;
                    const statusColor = statusConfig[booking.status]?.color || 'bg-gray-100 text-gray-800 border-gray-200';
                    
                    return (
                      <motion.div
                        key={booking._id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ 
                          duration: 0.2, 
                          delay: index * 0.03,
                          ease: [0.25, 0.1, 0.25, 1] // Smooth easing curve
                        }}
                        whileHover={{ y: -2, transition: { duration: 0.15 } }}
                        onClick={() => handleCardClick(booking)}
                        className="bg-white rounded-xl shadow-md border border-ivory-200 p-5 hover:shadow-xl transition-shadow duration-200 cursor-pointer"
                      >
                        {/* Card Header */}
                        <div className="mb-4">
                          <div className="flex items-start justify-between mb-3">
                            <h3 className="text-4 font-semibold text-ivory-900 line-clamp-2 flex-1">
                              {booking.courseTitle || 'Course Booking'}
                            </h3>
                          </div>
                          
                          {/* Status Badge */}
                          <div className="flex flex-wrap gap-2 mb-3">
                            <span className={`px-2.5 py-1 rounded-full text-1 font-semibold border flex items-center space-x-1 ${statusColor}`}>
                              <StatusIcon className="h-3 w-3" />
                              <span>{statusConfig[booking.status]?.label || booking.status}</span>
                            </span>
                            {/* Only show substatus for pending bookings without completed payment */}
                            {booking.substatus &&
                              booking.status === 'pending' &&
                              !booking.paymentCompletedAt &&
                              substatusConfig[booking.substatus] && (
                              <span className={`px-2.5 py-1 rounded-full text-1 font-semibold border flex items-center space-x-1 ${substatusConfig[booking.substatus].color}`}>
                                {React.createElement(substatusConfig[booking.substatus].icon, { className: "h-3 w-3" })}
                                <span>{substatusConfig[booking.substatus].label}</span>
                              </span>
                            )}
                          </div>
                        </div>

                        {/* School Info */}
                        {booking.schoolName && (
                          <div className="flex items-center text-2 text-ivory-600 mb-3">
                            <MapPin className="h-4 w-4 mr-2 text-primary flex-shrink-0" />
                            <span className="font-medium truncate">{booking.schoolName}</span>
                          </div>
                        )}

                        {/* Key Info */}
                        <div className="space-y-2 mb-4">
                          {booking.selectedMonth && (
                            <div className="flex items-center text-2 text-ivory-600">
                              <Calendar className="h-4 w-4 mr-2 text-primary flex-shrink-0" />
                              <span className="truncate">
                                {new Date(booking.selectedMonth + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                              </span>
                            </div>
                          )}
                          <div className="flex items-center text-2 text-ivory-600">
                            <Calendar className="h-4 w-4 mr-2 text-primary flex-shrink-0" />
                            <span className="truncate">Submitted: {formatDate(booking.submittedAt)}</span>
                          </div>
                        </div>

                        {/* View Details Button */}
                        <div className="pt-4 border-t border-ivory-200">
                          <button className="w-full flex items-center justify-center space-x-2 text-primary hover:text-primary-dark font-semibold text-2 transition-colors">
                            <Eye className="h-4 w-4" />
                            <span>View Details</span>
                          </button>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>

                {/* Pagination */}
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalCount={totalCount}
                  itemsPerPage={9}
                  currentItemsCount={bookings.length}
                  onPageChange={setCurrentPage}
                  loading={loading}
                  showCount={true}
                />
              </>
            )}
          </motion.div>
        </div>
      </div>

    </>
  );
};

export default Bookings;
